package app.nestbridge.feature.children.presentation

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import app.nestbridge.app.util.launchSafely
import app.nestbridge.feature.careteam.usecase.FindAllCareTeamsUseCase
import app.nestbridge.domain.identity.usecase.GetCurrentUserUseCase
import app.nestbridge.feature.careteam.usecase.FindCareTeamsByChildIdUseCase
import app.nestbridge.feature.children.model.Child
import app.nestbridge.feature.children.usecase.FindAllChildrenUseCase
import app.nestbridge.feature.children.screen.ChildDetailScreen
import app.nestbridge.feature.children.screen.ChildListEvent
import app.nestbridge.feature.children.screen.ChildListResult
import app.nestbridge.feature.children.screen.ChildListScreen
import app.nestbridge.feature.children.screen.ChildListState
import app.nestbridge.feature.children.screen.CreateChildProfileResult
import app.nestbridge.feature.children.screen.CreateChildProfileScreen
import app.nestbridge.feature.invitations.usecase.GetPendingTeamCreationInvitationUseCase
import app.nestbridge.feature.invitations.screen.InvitationQrScanScreen
import app.nestbridge.feature.invitations.screen.InvitationResult
import com.slack.circuit.codegen.annotations.CircuitInject
import com.slack.circuit.foundation.rememberAnsweringNavigator
import com.slack.circuit.runtime.Navigator
import com.slack.circuit.runtime.presenter.Presenter
import me.tatarka.inject.annotations.Assisted
import me.tatarka.inject.annotations.Inject
import software.amazon.lastmile.kotlin.inject.anvil.AppScope

@CircuitInject(
    screen = ChildListScreen::class,
    scope = AppScope::class,
)
@Inject
class ChildListPresenter(
    @Assisted private val navigator: Navigator,
    private val findAllChildrenUseCase: FindAllChildrenUseCase,
    private val findAllCareTeamsUseCase: FindAllCareTeamsUseCase,
    private val findCareTeamsByChildIdUseCase: FindCareTeamsByChildIdUseCase,
    private val getPendingTeamCreationInvitationUseCase: GetPendingTeamCreationInvitationUseCase,
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
) : Presenter<ChildListState> {

    @Composable
    override fun present(): ChildListState {
        var children by remember { mutableStateOf<List<Child>>(emptyList()) }
        var childTeamCounts by remember { mutableStateOf<Map<String, Int>>(emptyMap()) }
        var childPendingInvitations by remember { mutableStateOf<Map<String, Boolean>>(emptyMap()) }
        var hasAnyChildWithTeam by remember { mutableStateOf(false) }
        var isLoading by remember { mutableStateOf(true) }
        var returningFromCreateChild by remember { mutableStateOf(false) }
        var shouldNavigateToCreateChild by remember { mutableStateOf(false) }

        suspend fun loadChildren() {
            isLoading = true
            val userId = getCurrentUserUseCase()?.id

            val hasAnyTeamWithCurrentUser = userId != null && findAllCareTeamsUseCase().any { team ->
                team.members.any { member -> member.userId == userId }
            }

            children = if (userId != null) {
                 findAllChildrenUseCase().filter { child ->
                    child.stewardUserIds.contains(userId)
                }
            } else {
                emptyList()
            }

            // Load team counts for each child
            val teamCounts = mutableMapOf<String, Int>()
            val pendingInvitations = mutableMapOf<String, Boolean>()

            children.forEach { child ->
                val teams = findCareTeamsByChildIdUseCase(child.id)
                teamCounts[child.id] = teams.size

                val invitationResult = getPendingTeamCreationInvitationUseCase(child.id)
                pendingInvitations[child.id] = invitationResult.getOrNull() != null
            }

            childTeamCounts = teamCounts
            childPendingInvitations = pendingInvitations
            hasAnyChildWithTeam = teamCounts.values.any { it > 0 }

            // "New" child in list matches UI rule: no teams and no pending invitation.
            val hasNewChildInList = children.any { child ->
                val teamCount = teamCounts[child.id] ?: 0
                val hasPendingInvitation = pendingInvitations[child.id] ?: false
                teamCount == 0 && !hasPendingInvitation
            }

            isLoading = false

            // Auto-pop only when user is already in at least one team and there are no new children in list.
            if (hasAnyTeamWithCurrentUser && !hasNewChildInList) {
                navigator.pop(ChildListResult(ChildListResult.Status.Completed))
                return
            }

            // If no children where user is steward, redirect to create child profile
            if (children.isEmpty() && !returningFromCreateChild) {
                returningFromCreateChild = true
                shouldNavigateToCreateChild = true
            }
        }

        val createChildNavigator = rememberAnsweringNavigator<CreateChildProfileResult>(navigator) { result ->
            // no operation
        }

        // Navigate to CreateChildProfileScreen if needed
        if (shouldNavigateToCreateChild) {
            shouldNavigateToCreateChild = false
            createChildNavigator.goTo(CreateChildProfileScreen(showQrCodeScanButton = true))
        }

        val qrScanNavigator = rememberAnsweringNavigator<InvitationResult>(navigator) {
            // When returning from QR scan, reload the children
            kotlinx.coroutines.MainScope().launchSafely(onError = { isLoading = false }) {
                loadChildren()
            }
        }

        fun onEvent(event: ChildListEvent) {
            when (event) {
                ChildListEvent.OnAppear -> {
                    kotlinx.coroutines.MainScope().launchSafely(onError = { isLoading = false }) {
                        loadChildren()
                    }
                }
                is ChildListEvent.SelectChild -> {
                    navigator.goTo(ChildDetailScreen(childId = event.childId))
                }
                ChildListEvent.AddChild -> {
                    createChildNavigator.goTo(CreateChildProfileScreen(showQrCodeScanButton = false))
                }
                ChildListEvent.ScanQRCode -> {
                    qrScanNavigator.goTo(InvitationQrScanScreen())
                }
                ChildListEvent.Skip -> {
                    navigator.pop(ChildListResult(ChildListResult.Status.Skipped))
                }
            }
        }

        return ChildListState(
            children = children,
            childTeamCounts = childTeamCounts,
            childPendingInvitations = childPendingInvitations,
            hasAnyChildWithTeam = hasAnyChildWithTeam,
            isLoading = isLoading,
            eventSink = ::onEvent,
        )
    }
}
